import { html, css, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { consume } from '@lit/context';

import '@awesome.me/webawesome/dist/components/button/button.js';

import NSAutoPaymentsCard from './card-base.js';
import JobSchema from '../../../../../models/job-schema.js';
import definition from '@job-schemas/public-ledger/auto-payments/charge-failed.json';
import { jobSchema } from '../../../job-schema.js';
import { AUTOPAY_CARD_OUTCOMES } from './presentations.js';
import { nsStripeServiceContext } from '../../../../../ns-services/contexts.js';
import { NSStripeService } from '../../../../../ns-services/ns-stripe-service.js';
import { formatCredits } from '../../../../../helpers/public-ledger-credits.js';
import { formatPence } from '../../../../../helpers/credit-pricing.js';
import { COMPANY_CARD_NOTICE, MANDATE_TEXT, cardDots, cardText } from '../../../../../helpers/auto-payments.js';
import { autopayCardAction, type ChangedCard } from '../../../../../helpers/auto-payments-api.js';
import { lambdaRefusal } from '../../../../../helpers/public-ledger-balance-api.js';
import {
  BANK_APPROVAL_TEXT,
  CHARGE_FAILED_REOPENED_TEXT,
  chargeFailedActions,
  chargesStoppedReason,
  chargeFailedFigures,
  chargeFailureText,
  confirmStep,
  monthLabel,
  type ChargeFailedAction,
} from '../../../../../helpers/auto-payment-cards.js';
import { CHANGE_CARD_INTRO, cardSavedText } from '../../../../../helpers/auto-payment-changes.js';
import { AutopayCardForm } from './card-form.js';

import '../../../../../ns-elements/ns-job-layout.js';
import '../../../../../ns-elements/ns-detail-list.js';

/**
 * Automatic payment failed (#25 phase 4, 02 "Failure", DECISION 2.6, 2.9, 2.13): an automatic charge on the company's saved
 * card was declined, or the bank wants the card's owner to confirm it. One card per purchase, claimed by one signatory.
 * Nothing retries by itself: Retry makes a new attempt (a new key reaches Stripe), completed by
 * public-ledger/autopay/card-action. When the bank wants to check the payment with the cardholder (3-D Secure), the card
 * is given straight to them (the person who saved the live card, while they are still a signatory; otherwise it is a
 * normal decline for the team): it says what Confirm payment is for, and they confirm the same payment here (Stripe's
 * own step runs in the page), retry it or change the card. A card raised again after a change of card wasn't made
 * (REOPENED) starts by saying so.
 *
 * Change card (#25 phase 6) opens the card form on this card (AutopayCardForm). Saving holds the new card under this
 * card's workflow, records the change (round 15: an account-signed public ledger record naming who changed the card, for
 * the account signatories to sign) and completes the job `change-card` (public-ledger/autopay/confirm-change-card), and the
 * card closes. Never refused for nodeStream's platform account (round 15). If the change isn't made (the record is
 * rejected, expires, is cancelled or fails, or the new card can't be put in use) the held card is freed and a new Automatic
 * payment failed card is raised for the same payment.
 * A signed stop (#40) closes it `stopped` (public-ledger/autopay/stop-finish).
 */
@customElement(NSPublicLedgerAutoPaymentsChargeFailed.is)
export default class NSPublicLedgerAutoPaymentsChargeFailed extends NSAutoPaymentsCard {
  static get is() {
    return 'ns-public-ledger-auto-payments-charge-failed';
  }

  static get schema(): JobSchema {
    // Every outcome is hidden: presentations.ts says whose each one is
    return jobSchema(definition, {
      outcomes: AUTOPAY_CARD_OUTCOMES['@ns/public-ledger-auto-payments-charge-failed'],
      icon: 'bank-card',
    });
  }

  static styles = [
    ...super.styles,
    css`
      .job-panel ns-detail-list {
        --ns-detail-list-gap: var(--wa-space-2xs);
      }
    `,
  ];

  @consume({ context: nsStripeServiceContext })
  private __stripe?: NSStripeService;

  // Change card opens the card form on this card
  @state()
  private __changing = false;

  private __form = new AutopayCardForm(this, {
    stripe: () => this.__stripe,
    token: () => this._auth?.token,
    jobId: () => this._job?.id,
    held: (card) => this.__held(card),
    refused: (err) => {
      this.showError(lambdaRefusal(err, 'The card could not be saved. Please try again.'));
    },
  });

  async connectedCallback() {
    // Back from the bank's check of a new card (3-D Secure): busy from the start, so nothing can be pressed while it loads
    const returning = this.__form.returningFromBank();
    if (returning) {
      this._busy = true;
      this.loading('Saving the card');
    }
    try {
      await super.connectedCallback();
      await this._loadPerson(this.__ownerId);
      // then hold it
      if (returning && this._job && !this._completed && this._claimed) {
        try {
          await this.__form.returnFromBank();
        } catch (err) {
          this.showError(lambdaRefusal(err, 'The card could not be saved. Please try again.'));
        }
      }
    } finally {
      if (returning) {
        this.loaded();
        this._busy = false;
      }
    }
  }

  private get __figures() {
    return chargeFailedFigures(this._job?.metadata);
  }

  // The card's owner now (the row), else the one the card was raised with
  private get __ownerId(): string | null {
    return this._row?.cardOwnerId ?? this.__figures.cardOwnerId;
  }

  private get __actions(): ChargeFailedAction[] {
    return chargeFailedActions({
      completed: this._completed || this._waiting !== null,
      claimed: this._claimed,
      failure: this.__figures.failure,
      personId: this._me,
      cardOwnerId: this.__ownerId,
      chargesStopped: chargesStoppedReason(this._row) !== null,
    });
  }

  private async __retry() {
    const job = this._job;
    if (!job) return;
    await this._serverAction(
      async () => {
        await autopayCardAction(this._auth?.token, job.id, 'retry');
        return 'The payment is being tried again. This job closes in a moment; if the payment fails again, a new job says so.';
      },
      'Trying the payment again',
      'The payment could not be tried again. Please try again.',
    );
  }

  /**
   * The card's owner confirms the same payment on-session: card-action confirm answers the intent's status, and while the
   * bank wants a step in the browser (3-D Secure) Stripe runs it here, then card-action confirmed reads the result.
   */
  private async __confirm() {
    const job = this._job;
    if (!job) return;
    const token = this._auth?.token;
    await this._serverAction(
      async () => {
        let answer = await autopayCardAction(token, job.id, 'confirm');
        if (confirmStep(answer) === 'BANK_STEP') {
          if (!this.__stripe) throw new Error('The bank’s check could not be started. Please try again.');
          this.changeLoadingMessage('Waiting for your bank');
          await this.__stripe.handleNextAction(answer.clientSecret as string);
          answer = await autopayCardAction(token, job.id, 'confirmed');
        }
        const step = confirmStep(answer);
        if (step === 'PAID') {
          return 'The payment has gone through. The credits are on their way and this job closes in a moment.';
        }
        if (step === 'PROCESSING') {
          return 'Your bank is still processing the payment. This job closes once it has gone through.';
        }
        this.showError(
          'Your bank didn’t confirm the payment. You can confirm it again, or press Retry for a new payment.',
        );
        return null;
      },
      'Confirming the payment',
      'The payment could not be confirmed. Please try again.',
    );
  }

  private __back() {
    this.__form.clean();
    this.__changing = false;
    this.clearError();
  }

  private async __addCard() {
    if (this._busy) return;
    this._busy = true;
    this.clearError();
    this.loading('Starting the card form');
    try {
      await this.__form.start();
    } catch (err) {
      this.showError(lambdaRefusal(err, 'The card form could not be started. Please try again.'));
    } finally {
      this.loaded();
      this._busy = false;
    }
  }

  private async __saveCard() {
    if (this._busy) return;
    this._busy = true;
    this.clearError();
    this.loading('Saving the card');
    try {
      await this.__form.save();
    } catch (err) {
      this.showError(lambdaRefusal(err, 'The card could not be saved. Please try again.'));
    } finally {
      this.loaded();
      this._busy = false;
    }
  }

  // The new card is held and the change recorded: the server completes this job, and the card closes
  private async __held(card: ChangedCard) {
    const job = this._job;
    if (!job) return;
    this.__changing = false;
    this._waiting = cardSavedText(cardDots(card));
    const updated = await this._db?.getById('job', job.id);
    if (updated) this._jobUpdated(updated as unknown as typeof job);
  }

  // Why the payment failed; when the bank wants to check it with the cardholder, the card is theirs and says what Confirm
  // payment is for
  private __renderFailure(): TemplateResult {
    const figures = this.__figures;
    if (figures.failure === 'AUTH_REQUIRED' && this.__ownerId && this.__ownerId === this._me) {
      return html`
        <p>${BANK_APPROVAL_TEXT}</p>
      `;
    }
    return html`
      <p>${chargeFailureText(figures.failure, figures.declineCode)}</p>
    `;
  }

  // "The new card": approved (round 4 follow-ups, small string 4), as change-card's
  private __renderNewCard(): TemplateResult | typeof nothing {
    if (!this.__changing || this._waiting !== null || this._completed) return nothing;
    return html`
      <div class="job-panel">
        <h3>The new card</h3>
        <p>${CHANGE_CARD_INTRO}</p>
        <p><strong>${COMPANY_CARD_NOTICE}</strong></p>
        <p class="note">${MANDATE_TEXT}</p>
      </div>
      ${this.__form.clientSecret
        ? html`
            <div class="job-panel">
              <slot name="stripe-payment-form"></slot>
            </div>
          `
        : nothing}
    `;
  }

  render() {
    const figures = this.__figures;
    const card = cardText({ cardBrand: figures.cardBrand, cardLast4: figures.cardLast4 }) ?? cardText(this._row);
    const stopped = chargesStoppedReason(this._row);
    return html`
      <ns-job-layout>
        <p slot="intro">An automatic payment for your company’s nodeStream credits didn’t go through.</p>
        ${figures.reopened && !this._completed && this._waiting === null
          ? html`
              <div class="job-panel" role="status">
                <p>${CHARGE_FAILED_REOPENED_TEXT}</p>
              </div>
            `
          : nothing}
        ${this._renderWaiting() ??
        (this._claimed || this._completed
          ? html`
              <div class="job-panel">
                <h3>What happened</h3>
                ${this.__renderFailure()}
                ${stopped
                  ? html`
                      <p>${stopped}</p>
                    `
                  : nothing}
              </div>
            `
          : this._renderClaimNote('deal with the payment'))}
        ${this.__renderNewCard()}
        <div class="job-panel">
          <h3>The payment</h3>
          <ns-detail-list
            .rows=${[
              { label: 'Amount', value: figures.amountPence !== null ? formatPence(figures.amountPence) : undefined },
              { label: 'Credits', value: figures.credits !== null ? formatCredits(figures.credits) : undefined },
              { label: 'Card', value: card ?? undefined },
              { label: 'Month', value: monthLabel(figures.month) ?? undefined },
            ]}
          ></ns-detail-list>
        </div>
        <div slot="aside" class="primary-info">
          <span class="info-icon" aria-hidden="true">i</span>
          <h3>What to do</h3>
          <p>
            Automatic charges to the card have stopped. Nothing is tried again by itself: when the cause is fixed, press
            Retry to make a new payment.
          </p>
          <p>
            When the bank asks for the payment to be confirmed, only the signatory who saved the card can do it, from
            this job.
          </p>
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    // The card form on this card: Back closes it, and its own action is the filled button
    if (this.__changing && this._waiting === null && !this._completed) {
      return html`
        <wa-button
          variant="brand"
          appearance="outlined"
          type="button"
          ?disabled=${this._busy}
          @click=${() => this.__back()}
        >
          Back
        </wa-button>
        ${this.__form.clientSecret
          ? html`
              <wa-button variant="brand" type="button" ?disabled=${this._busy} @click=${() => this.__saveCard()}>
                Save card
              </wa-button>
            `
          : html`
              <wa-button variant="brand" type="button" ?disabled=${this._busy} @click=${() => this.__addCard()}>
                Add card
              </wa-button>
            `}
      `;
    }
    const primary = this.__actions.find((action) => action !== 'CLAIM');
    const appearance = (action: ChargeFailedAction) => (primary === action ? 'accent' : 'outlined');
    const buttons: Record<ChargeFailedAction, TemplateResult> = {
      CLAIM: this._renderClaimButton(),
      CONFIRM: html`
        <wa-button variant="brand" type="button" ?disabled=${this._busy} @click=${() => this.__confirm()}>
          Confirm payment
        </wa-button>
      `,
      RETRY: html`
        <wa-button
          variant="brand"
          appearance=${appearance('RETRY')}
          type="button"
          ?disabled=${this._busy}
          @click=${() => this.__retry()}
        >
          Retry
        </wa-button>
      `,
      CHANGE_CARD: html`
        <wa-button
          variant="brand"
          appearance=${appearance('CHANGE_CARD')}
          type="button"
          ?disabled=${this._busy}
          @click=${() => (this.__changing = true)}
        >
          Change card
        </wa-button>
      `,
    };
    return html`
      ${this.__actions.map((action) => buttons[action])}
    `;
  }
}
